import { css } from 'lit';

export const baseProjectViewStyles = css`

  /*=== HOST ===*/

  :host {
    background: linear-gradient(
      180deg,
      var(--theme-color-primary-4, var(--teal-2)) 0,
      var(--view-gradient-end-color) 200px
    );
    --view-gradient-end-color: var(--gray-1);

    --theme-color-primary-0: color-mix(in hsl, var(--theme-color-primary) 10%, white);
    --theme-color-primary-1: color-mix(in hsl, var(--theme-color-primary) 15%, white);
    --theme-color-primary-2: color-mix(in hsl, var(--theme-color-primary) 20%, white);
    --theme-color-primary-3: color-mix(in hsl, var(--theme-color-primary) 50%, white);
    --theme-color-primary-4: color-mix(in hsl, var(--theme-color-primary) 80%, white);
    --theme-color-primary-5: color-mix(in hsl, var(--theme-color-primary) 100%, white);
    --theme-color-primary-6: color-mix(in hsl, var(--theme-color-primary) 80%, black);
    --theme-color-primary-7: color-mix(in hsl, var(--theme-color-primary) 50%, black);
    --theme-color-primary-8: color-mix(in hsl, var(--theme-color-primary) 30%, black);
    --theme-color-primary-9: color-mix(in hsl, var(--theme-color-primary) 15%, black);

    --theme-color-secondary-0: color-mix(in hsl, var(--theme-color-secondary) 10%, white);
    --theme-color-secondary-1: color-mix(in hsl, var(--theme-color-secondary) 15%, white);
    --theme-color-secondary-2: color-mix(in hsl, var(--theme-color-secondary) 20%, white);
    --theme-color-secondary-3: color-mix(in hsl, var(--theme-color-secondary) 50%, white);
    --theme-color-secondary-4: color-mix(in hsl, var(--theme-color-secondary) 80%, white);
    --theme-color-secondary-5: color-mix(in hsl, var(--theme-color-secondary) 100%, white);
    --theme-color-secondary-6: color-mix(in hsl, var(--theme-color-secondary) 80%, black);
    --theme-color-secondary-7: color-mix(in hsl, var(--theme-color-secondary) 50%, black);
    --theme-color-secondary-8: color-mix(in hsl, var(--theme-color-secondary) 30%, black);
    --theme-color-secondary-9: color-mix(in hsl, var(--theme-color-secondary) 15%, black);

    /** Experimental Dark Mode */
    --dm-white: var(--white);
    --dm-gray-0: var(--gray-0);
    --dm-gray-1: var(--gray-1);
    --dm-gray-2: var(--gray-2);
    --dm-gray-3: var(--gray-3);
    --dm-gray-4: var(--gray-4);
    --dm-gray-5: var(--gray-5);
    --dm-gray-6: var(--gray-6);
    --dm-gray-7: var(--gray-7);
    --dm-gray-8: var(--gray-8);
    --dm-gray-9: var(--gray-9);
  }
  /* EXPERIMENTAL DARK MODE */
  :host(.dark-mode) {
    --view-gradient-end-color: var(--theme-color-primary-8);

    --dm-white: var(--gray-9);
    --dm-gray-0: var(--gray-9);
    --dm-gray-1: var(--gray-8);
    --dm-gray-2: var(--gray-7);
    --dm-gray-3: var(--gray-6);
    --dm-gray-4: var(--gray-5);
    --dm-gray-5: var(--gray-4);
    --dm-gray-6: var(--gray-3);
    --dm-gray-7: var(--gray-2);
    --dm-gray-8: var(--gray-1);
    --dm-gray-9: var(--gray-0);
  }

  /*=== BASE LAYOUT ===*/

  view-content {
    max-width: none;
  }

  /* EXPERIMENTAL DARK MODE */
  view-content.dark-mode {
    color: white;
  }

  project-subheader,
  column-wrapper,
  header-bar:not(.nested),
  sl-tab-panel:not(.bp-clear),
  sl-tab-group#project-tabs::part(nav) {
    max-width: var(--q1-view-width);
    margin-left: auto !important;
    margin-right: auto !important;
  }

  /*=== TABS ===*/

  tab-wrapper {
    display: block;
  }
  sl-tab-group#project-tabs::part(nav) {
    width: 100%;

    --indicator-color: var(--theme-color-primary-4);
    --track-color: transparent;
    --track-width: 4px;
  }
  sl-tab-group#project-tabs::part(body) {
    overflow: visible;
  }

  sl-tab::part(base) {
    font-size: 0.9rem;
    transition: all 0.3s;
    color: var(--dm-gray-6);
  }
  sl-tab:hover::part(base) {
    color: var(--theme-color-primary-6);
  }
  view-content.dark-mode sl-tab:hover::part(base) {
    color: var(--theme-color-primary-3);
  }
  sl-tab[active]::part(base) {
    color: var(--theme-color-primary-8);
    font-weight: 800;
    font-size: 1.1rem;
  }
  .dark-mode sl-tab[active]::part(base) {
    color: var(--theme-color-primary-1);
  }

  /*=== SHOELACE ELEMENTS ===*/

  sl-divider.full-width {
    margin: 0 calc(-1 * var(--view-item-spacing));
  }

  sl-divider.menu-divider {
    margin: var(--sl-spacing-x-small) 0;
  }

  /*=== CUSTOM ELEMENTS ===*/

  column-wrapper {
    margin-top: 0;
  }

  empty-alert {
    color: var(--dm-gray-6);
    background: var(--dm-gray-2);
    border: 1px dashed var(--dm-gray-4);
  }

  notice-panel {
    margin-top: 5rem;
  }

  notice-panel .inner {
    padding-left: 3rem;
    padding-right: 3rem;
  }

  empty-alert {
    color: var(--dm-gray-6);
    background: var(--dm-gray-2);
    border: 1px dashed var(--dm-gray-4);
  }

  @media(max-width: 650px) {
    q1-membership-list.full-width {
      margin: 0 calc(-2rem - 2px);
    }
    q1-membership-list.full-width::part(base) {
      border-radius: 0;
    }
  }

  /*=== ERROR STATE ===*/

  q1-project-card.project-error {
    max-width: 50rem;
    margin-top: 2rem;
    margin-left: auto;
    margin-right: auto;
  }
  q1-project-card.project-error::part(base) {
    margin: 0;
  }

  notice-panel p.footer {
    margin: 0;
    color: var(--gray-6);
    --link-color-default: var(--gray-6);
    --link-color-visited: var(--gray-6);
    --link-color-focus: var(--gray-8);
  }
  
`;